import Transport from "winston-transport";
import Batcher from "./batcher";
import { MESSAGE } from "triple-beam";

interface LokiTransportOptions extends Transport.TransportStreamOptions {
  host: string;
  headers?: Record<string, string>;
  /** @default 5000 (milliseconds) */
  interval?: number;
  batching?: boolean;
  clearOnError?: boolean;
  replaceTimestamp?: boolean;
  gracefulShutdown?: boolean;
  timeout?: number;
  labels?: Record<string, string>;
}

interface LogInfo {
  level: string;
  message: string;
  label?: string;
  labels?: Record<string, string>;
  timestamp?: number;
  [MESSAGE]?: string;
  [key: string]: unknown;
}

interface LogEntry {
  labels: Record<string, string>;
  entries: Array<{
    ts: number;
    line: string;
  }>;
}

/**
 * A Winston transport for Grafana Loki.
 *
 * @class CustomLokiTransport
 * @extends {Transport}
 *
 * @link https://github.com/scumpah/winston-loki/blob/v6.0.0/index.js
 */
export class CustomLokiTransport extends Transport {
  batcher: Batcher;
  private labels?: Record<string, string>;
  private useCustomFormat: boolean;

  /**
   * Creates an instance of CustomLokiTransport.
   * @param options - Transport configuration options
   */
  constructor(options: LokiTransportOptions) {
    super(options);

    // Pass all the given options to batcher
    this.batcher = new Batcher({
      host: options.host,
      headers: options.headers || {},
      interval: options.interval,
      batching: options.batching !== false,
      clearOnError: options.clearOnError,
      gracefulShutdown: options.gracefulShutdown !== false,
      timeout: options.timeout,
    });

    this.useCustomFormat = options.format !== undefined;
    this.labels = options.labels;
  }

  /**
   * An overwrite of winston-transport's log(),
   * which the Winston logging library uses
   * when pushing logs to a transport.
   *
   * @param info - Log information object
   * @param callback - Callback function to signal completion
   */
  log(info: LogInfo, callback: () => void): void {
    // Immediately tell Winston that this transport has received the log.
    setTimeout(() => {
      this.emit("logged", info);
    }, 0);

    // Deconstruct the log
    const { label, labels, timestamp, level, ...rest } = info;

    // build custom labels if provided
    let event = rest.message;
    event = event ? event.split(" ")[0].toLowerCase() : "generic";

    let lokiLabels: Record<string, string> = { level, event };

    lokiLabels = Object.assign(lokiLabels, labels);

    if (this.labels) {
      lokiLabels = Object.assign(lokiLabels, this.labels);
    } else {
      lokiLabels["job"] = label || "default";
    }

    // follow the format provided
    // message property will come inside rest object, no need to add it separately
    const line = this.useCustomFormat
      ? info[MESSAGE] || ""
      : `${rest && Object.keys(rest).length > 0 ? JSON.stringify(rest) : ""}`;

    // Construct the log to fit Grafana Loki's accepted format
    const logEntry: LogEntry = {
      labels: lokiLabels,
      entries: [
        {
          ts: timestamp || Date.now().valueOf(),
          line,
        },
      ],
    };

    // Pushes the log to the batcher
    this.batcher.pushLogEntry(logEntry);

    // Trigger the optional callback
    callback();
  }

  /**
   * Send batch to loki when clean up
   */
  close(): void {
    this.batcher.close();
  }
}
